import React, { useState, useCallback } from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Collapse,
  Paper
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

/**
 * RegionCommuneSelector component renders a hierarchical, collapsible checklist.
 *
 * @param {Object} props
 * @param {Array} [props.regions=[]] - The hierarchical geographic data.
 * @param {Array} [props.selectedCommunesIds=[]] - Array of currently selected commune IDs.
 * @param {function} [props.onChange] - Callback fired when selection changes.
 */
export default function RegionCommuneSelector({
  regions = [],
  selectedCommunesIds = [],
  onChange = () => {}
}) {
  const [openRegions, setOpenRegions] = useState({});

  const handleToggleRegionView = useCallback((regionId) => {
    setOpenRegions((prev) => ({
      ...prev,
      [regionId]: !prev[regionId]
    }));
  }, []);

  const handleToggleCommune = useCallback((communeId) => {
    const safeSelected = Array.isArray(selectedCommunesIds) ? selectedCommunesIds : [];
    const currentIndex = safeSelected.indexOf(communeId);
    const newSelected = [...safeSelected];

    if (currentIndex === -1) {
      newSelected.push(communeId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onChange(newSelected);
  }, [selectedCommunesIds, onChange]);

  const handleToggleRegionSelection = useCallback((region) => {
    const safeCommunes = region.communes || [];
    const regionCommuneIds = safeCommunes.map((c) => c.id);

    const safeSelected = Array.isArray(selectedCommunesIds) ? selectedCommunesIds : [];

    const isAllSelected = regionCommuneIds.every((id) =>
      safeSelected.includes(id)
    );

    let newSelected;

    if (isAllSelected) {
      newSelected = safeSelected.filter(
        (id) => !regionCommuneIds.includes(id)
      );
    } else {
      const missingCommunes = regionCommuneIds.filter(
        (id) => !safeSelected.includes(id)
      );
      newSelected = [...safeSelected, ...missingCommunes];
    }

    onChange(newSelected);
  }, [selectedCommunesIds, onChange]);

  return (
    <Paper elevation={0} sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0' }}>
      <List component="nav" disablePadding>
        {regions?.map((region) => {
          const safeCommunes = region.communes || [];
          const regionCommuneIds = safeCommunes.map((c) => c.id);
          const safeSelected = Array.isArray(selectedCommunesIds) ? selectedCommunesIds : [];

          const selectedCount = regionCommuneIds.filter((id) =>
            safeSelected.includes(id)
          ).length;

          const isAllSelected = selectedCount === regionCommuneIds.length && regionCommuneIds.length > 0;
          const isIndeterminate = selectedCount > 0 && selectedCount < regionCommuneIds.length;
          const isOpen = Boolean(openRegions[region.id]);

          return (
            <React.Fragment key={region.id}>
              <ListItemButton
                onClick={() => handleToggleRegionView(region.id)}
                sx={{ bgcolor: isOpen ? 'action.hover' : 'inherit' }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    disableRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleRegionSelection(region);
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={region.name}
                  secondary={`${selectedCount} / ${regionCommuneIds.length} seleccionadas`}
                />
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>

              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {safeCommunes.map((commune) => {
                    const isCommuneSelected = safeSelected.includes(commune.id);

                    return (
                      <ListItemButton
                        key={commune.id}
                        sx={{ pl: 4 }}
                        onClick={() => handleToggleCommune(commune.id)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={isCommuneSelected}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText primary={commune.name} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </Paper>
  );
}